/**
 * @file Browser-facing HTTP routes that every consumer of
 *       `@labcraft/account` mounts via `Proxy` — the shared
 *       surface that lab / www expose at `/api/account/*`.
 * @module @labcraft/account/shared/proxy-routes
 *
 * Distinct from `./routes.ts`, which carries the loopback wire
 * contracts between consumers and the account daemon
 * (`POST /api/:provider/authorize` etc., consumed via `Account` client).
 * The routes here are the consumer-side surface the SPA hits — they
 * delegate to the loopback ones inside `Proxy`.
 *
 * The /me response shape is intentionally `.passthrough()` on the
 * learner object so consumers that join external data into the
 * response (lab adds `currentChapter` from `learner_progress`) can do
 * so without forcing every consumer to rewrite the schema. The
 * consumer's `shapeMeUser` hook owns the augmentation.
 */

import { z } from 'zod'
import { AcquisitionSchema, CoreAnalytics, defineRoute } from '@labcraft/core'

////////////////////////////////////////////////////////////////////////////////
// Provider ids
////////////////////////////////////////////////////////////////////////////////

/**
 * Discriminated set of provider ids the consumer can dispatch on.
 * Mirrors `@labcraft/account/shared/routes.ts`'s `SigninRequestSchema`
 * discriminator.
 */
export const ProviderIdSchema = z.enum(['google', 'github', 'email'])

/** Parsed `ProviderId`. */
export type ProviderId = z.infer<typeof ProviderIdSchema>

////////////////////////////////////////////////////////////////////////////////
// Common response shapes
////////////////////////////////////////////////////////////////////////////////

/**
 * Response of `POST /account/email/authorize`. Always `'sent'` — the
 * silent-`sent` shape blocks address-existence enumeration.
 */
export const SentResponseSchema = z.object({
  status: z.literal('sent'),
})

/** Parsed `SentResponse`. */
export type SentResponse = z.infer<typeof SentResponseSchema>

/**
 * Response of `POST /account/signout`. Minimal acknowledgement; the
 * cookie clear is the side effect that matters.
 */
export const SignoutResponseSchema = z.object({
  status: z.literal('ok'),
})

/** Parsed `SignoutResponse`. */
export type SignoutResponse = z.infer<typeof SignoutResponseSchema>

////////////////////////////////////////////////////////////////////////////////
// Email signin
////////////////////////////////////////////////////////////////////////////////

/**
 * Body of `POST /account/email/authorize`. Sign-up + sign-in resolve to
 * the same call.
 *
 * @property email - Recipient email; the magic-link delivery target.
 * @property next - Optional same-origin redirect target embedded in
 *   the account-service flow row. Sanitized server-side.
 * @property timezone - Optional browser IANA zone (`Intl.…timeZone`),
 *   forwarded to the account daemon's signin and written to
 *   `learners.timezone` on first sign-up. Best-effort; the daemon
 *   drops any unrecognized value.
 * @property acquisitionSource - Optional granular acquisition source
 *   captured on the anonymous `/authorize` landing (#1214). An open
 *   sanitized string (utm token or referrer host) — the browser classifies
 *   referrer + utm before this crosses the wire, so no raw referrer/utm
 *   string is ever sent. Forwarded to the account daemon's signin and
 *   written to `learners.acquisition_source` on first sign-up.
 * @property acquisitionCategory - Optional derived coarse acquisition
 *   category (`campaign` | `organic` | `referral` | `direct`). Forwarded
 *   alongside the source and written to `learners.acquisition_category` on
 *   first sign-up.
 * @property acquisitionCampaign - Optional specific campaign name (the
 *   sanitized `utm_campaign` token), present only on a campaign visit that
 *   carried a `utm_campaign` tag. Shares the `source` charset/length bound;
 *   written to `learners.acquisition_campaign` on first sign-up.
 */
export const EmailSigninRequestSchema = z.object({
  email: z.string().email(),
  next: z.string().optional(),
  timezone: z.string().max(64).optional(),
  acquisitionSource: CoreAnalytics.AcquisitionSourceSchema.optional(),
  acquisitionCategory: CoreAnalytics.AcquisitionCategorySchema.optional(),
  acquisitionCampaign: CoreAnalytics.AcquisitionSourceSchema.optional(),
})

/** Parsed `EmailSigninRequest`. */
export type EmailSigninRequest = z.infer<typeof EmailSigninRequestSchema>

/**
 * `POST /account/email/authorize` — mint a magic-link token via account-
 * service and mail it. Always returns `{ status: 'sent' }`.
 */
export const EmailSigninRoute = defineRoute({
  method: 'POST',
  path: '/api/account/email/authorize',
  request: EmailSigninRequestSchema,
  response: SentResponseSchema,
  description:
    'Mail a magic-link auth token. Always returns sent regardless of whether the address exists.',
})

/**
 * `GET /account/email/callback?token=…` — magic-link landing. 302s to
 * the original `next` (or `/auth/expired` on failure).
 */
export const EmailCallbackRoute = defineRoute({
  method: 'GET',
  path: '/api/account/email/callback',
  response: z.void(),
  description:
    'Magic-link landing. Consumes the token and redirects to the next URL with the session cookie set.',
})

////////////////////////////////////////////////////////////////////////////////
// Google OIDC
////////////////////////////////////////////////////////////////////////////////

/** `GET /account/google/authorize?next=…` — kick off the Google flow. */
export const GoogleSigninRoute = defineRoute({
  method: 'GET',
  path: '/api/account/google/authorize',
  response: z.void(),
  description:
    'Begin a Google OIDC auth flow (sign-up + sign-in unified). 302 → Google authorize URL.',
})

/** `GET /account/google/callback?code=…&state=…` — Google's redirect target. */
export const GoogleCallbackRoute = defineRoute({
  method: 'GET',
  path: '/api/account/google/callback',
  response: z.void(),
  description:
    "Consume Google's authorization code, mint the consumer session, and redirect to next.",
})

////////////////////////////////////////////////////////////////////////////////
// GitHub OAuth
////////////////////////////////////////////////////////////////////////////////

/** `GET /account/github/authorize?next=…` — kick off the GitHub flow. */
export const GithubSigninRoute = defineRoute({
  method: 'GET',
  path: '/api/account/github/authorize',
  response: z.void(),
  description:
    'Begin a GitHub OAuth flow (sign-up + sign-in unified). 302 → GitHub authorize URL.',
})

/** `GET /account/github/callback?code=…&state=…` — GitHub's redirect target. */
export const GithubCallbackRoute = defineRoute({
  method: 'GET',
  path: '/api/account/github/callback',
  response: z.void(),
  description:
    "Consume GitHub's authorization code, mint the consumer session, and redirect to next.",
})

////////////////////////////////////////////////////////////////////////////////
// Signout
////////////////////////////////////////////////////////////////////////////////

/** `POST /account/signout` — clear the session cookie + Redis row. */
export const SignoutRoute = defineRoute({
  method: 'POST',
  path: '/api/account/signout',
  response: SignoutResponseSchema,
  description: 'Clear the session cookie and delete the matching session row.',
})

////////////////////////////////////////////////////////////////////////////////
// /me
////////////////////////////////////////////////////////////////////////////////

/**
 * Base /me learner shape every consumer guarantees. `.passthrough()`
 * lets consumers add extra fields without rewriting the schema — lab
 * adds `currentChapter` via its `shapeMeUser` hook in `ProxyDeps`.
 *
 * The fields here are the ones the session payload caches at signin so
 * `/me` reads don't round-trip the account daemon on the hot path.
 *
 * @property id - UUID primary key.
 * @property email - Lower-cased verified email.
 * @property displayName - Free-form display name.
 * @property emailVerifiedAt - ISO-8601 timestamp; nullable for shape
 *   stability across providers that don't surface a verification time.
 * @property createdAt - ISO-8601 row-insertion timestamp (signup
 *   instant). Surfaced so the lab SPA can bucket it to a `YYYY-Www`
 *   cohort week for `umami.identify()` (umbrella #1213, Phase 2); the raw
 *   timestamp never reaches Umami, only the derived bucket.
 * @property status - Access state.
 * @property profileCompletedAt - ISO-8601 timestamp of the learner's
 *   first `/account/update` POST.
 * @property avatarUrl - Provider-hosted profile picture URL or null.
 * @property acquisition - Captured acquisition attribution (`source` +
 *   `category`), or null when never captured. Surfaced so the lab SPA can
 *   flatten it into `umami.identify()` (umbrella #1213, Phase 2). Nested
 *   in the /me JSON response; the SPA splits it to two scalar identify
 *   props.
 */
export const BaseMeUserSchema = z
  .object({
    id: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string(),
    emailVerifiedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    status: z.enum(['active', 'inactive', 'banned']),
    profileCompletedAt: z.string().datetime().nullable(),
    avatarUrl: z.string().url().nullable(),
    acquisition: AcquisitionSchema.nullable(),
  })
  .passthrough()

/** Parsed base /me learner (consumer-augmented). */
export type BaseMeUser = z.infer<typeof BaseMeUserSchema>

/**
 * `GET /account/me` response shape. Returns the authenticated learner
 * when the session cookie is valid; `{ learner: null }` for anonymous.
 * Never errors — the SPA reads it on every page load.
 */
export const MeResponseSchema = z.object({
  learner: BaseMeUserSchema.nullable(),
})

/** Parsed `MeResponse`. */
export type MeResponse = z.infer<typeof MeResponseSchema>

/** `GET /account/me` — returns the current learner or `null`. */
export const MeRoute = defineRoute({
  method: 'GET',
  path: '/api/account/me',
  response: MeResponseSchema,
  description:
    'Return the current authenticated learner, or `{ learner: null }` for anonymous requests.',
})
